import { addDays, addMonths, addYears, differenceInDays } from 'date-fns';
import type { Selector } from 'reselect';
import Cookies from 'universal-cookie';

import { FeatureFlag } from '@swf/core/enums';
import { select } from '@swf/core/store';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import CookieNames from '@swf/core/types/Cookies';
import { tryFetch } from '@swf/core/utils/api';

const cookies = new Cookies();

export enum NpsDismissalReason {
  Dismissed = 0,
  ThankYouDismissed = 1,
  AutoDismissed = 2
}

export interface NpsState {
  isIdle: boolean;
  dismissed: boolean;
  submitted: boolean;
  minSevenDays: boolean;
  recentInteraction: boolean;
  loggedInBufferElapsed: boolean;
  valueSubmitted: number | undefined;
}

interface NpsModel extends ModelConfig<NpsState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const reducers = {
  display: (state: NpsState) => ({ ...state, isIdle: true }),
  dismissed: (state: NpsState) => ({ ...state, dismissed: true }),
  submitted: (state: NpsState, value: number) => ({
    ...state,
    submitted: true,
    valueSubmitted: value
  }),
  firstAccessAcceptable: (state: NpsState) => ({ ...state, minSevenDays: true }),
  recentInteraction: (state: NpsState) => ({ ...state, recentInteraction: true }),
  loggedInBufferElapsed: (state: NpsState) => ({ ...state, loggedInBufferElapsed: true })
};

const selectors = (slice: Slicer<NpsState>, createSelector: SelectorCreator) => ({
  isDisplayableToUser() {
    return createSelector(
      slice,
      (rootState) => select.site.isEmbedded(rootState),
      (rootState) => select.modal.getCurrent(rootState),
      (rootState) => select.betSlip.selections(rootState),
      (
        { submitted, dismissed, minSevenDays, recentInteraction, loggedInBufferElapsed },
        isEmbedded,
        modalShowing,
        betslipSelections
      ) =>
        !dismissed &&
        !submitted &&
        minSevenDays &&
        !recentInteraction &&
        !isEmbedded &&
        !modalShowing &&
        !betslipSelections.length
    );
  },
  justSubmitted() {
    return slice(({ submitted, dismissed }) => !dismissed && submitted);
  },
  valueSubmitted() {
    return slice(({ valueSubmitted }) => valueSubmitted);
  },
  userIdle() {
    return slice(({ isIdle }) => isIdle);
  },
  userLoggedInBufferElapsed() {
    return slice(({ loggedInBufferElapsed }) => loggedInBufferElapsed);
  },
  shouldDisplay(this: {
    isDisplayableToUser: Selector<RootState, boolean>;
    userLoggedInBufferElapsed: Selector<RootState, boolean>;
    userIdle: Selector<RootState, boolean>;
  }) {
    return createSelector(
      this.isDisplayableToUser,
      this.userLoggedInBufferElapsed,
      this.userIdle,
      (displayable, loggedInBufferElapsed, userIdle) =>
        displayable && loggedInBufferElapsed && userIdle
    );
  },
  getSubmissionQuery() {
    return createSelector(
      (rootState: RootState) => select.session.getWebId(rootState),
      (rootState) => select.page.getVerticalName(rootState).toLowerCase(),
      (rootState) => select.site.getBrandName(rootState)?.replace(' ', '').toLowerCase(),
      (webId, verticalName, brandName) => ({
        '437288X1254X9900': webId,
        '437288X1254X9901': 'web',
        '437288X1254X9902': verticalName,
        '437288X1254X9905': brandName
      })
    );
  },
  getDismissalQuery() {
    return createSelector(
      (rootState: RootState) => select.session.getWebId(rootState),
      (rootState) => select.page.getVerticalName(rootState).toLowerCase(),
      (rootState) => select.site.getBrandName(rootState)?.replace(' ', '').toLowerCase(),
      (_rootState: RootState, dismissalReason: NpsDismissalReason) => dismissalReason,
      (webId, verticalName, brandName, dismissalReason) => ({
        '988931X1256X9921': webId,
        '988931X1256X9922': 'web',
        '988931X1256X9923': verticalName,
        '988931X1256X9924': brandName,
        '988931X1256X9925': dismissalReason
      })
    );
  },
  getFollowUpQuery() {
    return createSelector(
      (rootState: RootState) => select.session.getWebId(rootState),
      (rootState) => select.nps.valueSubmitted(rootState),
      (webId, valueSubmitted) => ({
        '321927X1258X9937': webId,
        '321927X1258X9936': valueSubmitted
      })
    );
  }
});

const effects = {
  submit(
    { value, submissionUrl }: { value: number; submissionUrl: string },
    { dispatch, rootState }: EffectParams
  ) {
    const baseQuery = select.nps.getSubmissionQuery(rootState);
    const query = { ...baseQuery, '437288X1254X9906': value };
    const url = new URL(submissionUrl);
    Object.entries(query).forEach(([fieldName, fieldValue]) =>
      url.searchParams.append(fieldName, String(fieldValue))
    );

    try {
      tryFetch(url.toString(), {
        method: 'GET'
      });
    } catch (e) {
      // do nothing
    }

    dispatch.nps.submitted(value);

    cookies.set(CookieNames.NPS_INTERACTION, 'submit', {
      path: '/',
      expires: addMonths(new Date(), 3)
    });

    setTimeout(
      dispatch.nps.dismissed,
      select.feature.check(rootState, FeatureFlag.NPSPhase2) ? 10000 : 3000
    );
  },
  dismiss(
    {
      dismissalUrl,
      dismissalReason
    }: { dismissalUrl: string; dismissalReason: NpsDismissalReason },
    { dispatch, rootState }: EffectParams
  ) {
    const query = select.nps.getDismissalQuery(rootState, dismissalReason);
    const url = new URL(dismissalUrl);
    Object.entries(query).forEach(([fieldName, fieldValue]) =>
      url.searchParams.set(fieldName, String(fieldValue))
    );

    try {
      tryFetch(url.toString(), {
        method: 'GET'
      });
    } catch (e) {
      // do nothing
    }

    if (dismissalReason === NpsDismissalReason.Dismissed) {
      cookies.set(CookieNames.NPS_INTERACTION, 'dismiss', {
        path: '/',
        expires: addDays(new Date(), 7)
      });
    }

    if (dismissalReason !== NpsDismissalReason.AutoDismissed) {
      dispatch.nps.dismissed();
    }
  },
  accessFollowUp(followUpUrl: string, { dispatch, rootState }: EffectParams) {
    const query = select.nps.getFollowUpQuery(rootState);
    const url = new URL(followUpUrl);
    Object.entries(query).forEach(([fieldName, fieldValue]) =>
      url.searchParams.set(fieldName, String(fieldValue))
    );

    window.open(url.toString(), '_blank', 'noopener');
    dispatch.nps.dismissed();
  },
  setFirstAccess(_: undefined, { dispatch }: EffectParams) {
    const firstAccessCookieValue = cookies.get(CookieNames.FIRST_LOGIN);
    if (!firstAccessCookieValue) {
      cookies.set(CookieNames.FIRST_LOGIN, new Date(), {
        path: '/',
        expires: addYears(new Date(), 10)
      });
    }
  },
  checkFirstAccess(_: undefined, { dispatch }: EffectParams) {
    const firstAccessCookieValue = cookies.get(CookieNames.FIRST_LOGIN);
    if (!firstAccessCookieValue) {
      return;
    }

    if (differenceInDays(new Date(), new Date(firstAccessCookieValue)) > 7) {
      dispatch.nps.firstAccessAcceptable();
    }
  },
  checkPreviousInteraction(_: undefined, { dispatch }: EffectParams) {
    const interactionCookie = cookies.get(CookieNames.NPS_INTERACTION);
    if (interactionCookie) {
      dispatch.nps.recentInteraction();
    }
  }
};

const nps: NpsModel = {
  state: {
    isIdle: false,
    dismissed: false,
    submitted: false,
    minSevenDays: false,
    recentInteraction: false,
    loggedInBufferElapsed: false,
    valueSubmitted: undefined
  },
  selectors,
  effects,
  reducers
};

export default nps;
